"use client";

import { useId, useRef } from "react";
import { motion, useInView } from "@/lib/motion";
import { GREEN } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useDeviceShade } from "./device-shade";

// House geometry: 100x100 plan, 30px walls, pyramid roof with 8px eaves.
// All faces are exact clip-path planes sharing the same wrapper, so every
// ridge and eave meets flush — no strokes, no rounding on the joints.
const ROOF_PITCH = 26.57;

interface Props {
  reduced: boolean;
  hovered: boolean;
}

export function ResidentialProxiesVisual({ reduced, hovered }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { margin: "100px 0px" });
  const active = !reduced && inView;
  const shade = useDeviceShade();
  const holeId = `${useId()}-pin-hole`;

  return (
    <div ref={rootRef} className="relative h-full w-full overflow-hidden">
      {/* Blueprint dotted grid */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(color-mix(in srgb, var(--foreground) 6%, transparent) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Ambient glow behind the house */}
      <div
        className={cn(
          "pointer-events-none absolute top-1/2 left-1/2 mt-[-70px] ml-[-110px] h-[150px] w-[220px] rounded-full blur-3xl transition-opacity duration-500",
          hovered ? "opacity-90" : "opacity-50"
        )}
        style={{ background: `radial-gradient(closest-side, ${GREEN}1A, transparent)` }}
      />

      {/* 3D stage — isometric house, centered */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ perspective: 1100 }}
      >
        <div
          className={cn(
            "relative mt-6 h-[132px] w-[132px]",
            !reduced && "transition-transform duration-500"
          )}
          style={{
            transformStyle: "preserve-3d",
            transform: hovered
              ? "rotateX(54deg) rotateZ(-34deg)"
              : "rotateX(57deg) rotateZ(-38deg)",
          }}
        >
          {/* Ghost plan — dashed wireframe outlines beneath the house */}
          <motion.div className="absolute inset-0" style={{ transformStyle: "preserve-3d" }}>
            <div
              className="border-foreground/20 absolute inset-0 rounded-[14px] border border-dashed"
              style={{ transform: "translateZ(-14px)" }}
            />
            <div
              className="border-foreground/10 absolute inset-0 rounded-[14px] border border-dashed"
              style={{ transform: "translateZ(-28px)" }}
            />
          </motion.div>

          {/* The house */}
          <motion.div
            className="absolute top-[16px] left-[16px] h-[100px] w-[100px]"
            style={{ transformStyle: "preserve-3d" }}
            initial={{ z: 0 }}
            animate={active ? { z: [0, 2.5, 0, -2.5, 0] } : undefined}
            transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
          >
            {/* Ground shadow */}
            <div
              className="absolute -inset-3 rounded-[14px] bg-black/50 blur-md"
              style={{ transform: "translateZ(-4px)" }}
            />

            {/* Body — walls hanging from the wall-top plane to the ground.
                One continuous gradient wraps the body: tones match exactly
                at every corner. */}
            <div
              className="absolute inset-0"
              style={{ transformStyle: "preserve-3d", transform: "translateZ(30px)" }}
            >
              {/* South wall, with door + windows */}
              <div
                className="absolute right-0 bottom-0 left-0 h-[30px]"
                style={{
                  transformOrigin: "bottom",
                  transform: "rotateX(90deg)",
                  background: `linear-gradient(90deg, ${GREEN}29, transparent 80%), linear-gradient(90deg, ${shade("#222529")} 0%, ${shade("#1a1d21")} 100%)`,
                }}
              >
                <div
                  className="absolute bottom-0 left-[44px] h-[17px] w-[13px] rounded-t-[2px]"
                  style={{ backgroundColor: shade("#0c0e10") }}
                />
                <div
                  className="absolute top-[9px] left-[13px] h-[9px] w-[13px] rounded-[1.5px]"
                  style={{ backgroundColor: shade("#0c0e10") }}
                />
                <div
                  className="absolute top-[9px] right-[13px] h-[9px] w-[13px] rounded-[1.5px]"
                  style={{ backgroundColor: shade("#0c0e10") }}
                />
              </div>
              {/* West wall — the lit left elevation */}
              <div
                className="absolute top-0 bottom-0 left-0 w-[30px]"
                style={{
                  transformOrigin: "left",
                  transform: "rotateY(90deg)",
                  background: `linear-gradient(180deg, ${GREEN}29, transparent 80%), linear-gradient(180deg, ${shade("#2a2e33")} 0%, ${shade("#222529")} 100%)`,
                }}
              />
              {/* East wall */}
              <div
                className="absolute top-0 right-0 bottom-0 w-[30px]"
                style={{
                  transformOrigin: "right",
                  transform: "rotateY(-90deg)",
                  background: `linear-gradient(180deg, ${shade("#101216")} 0%, ${shade("#1a1d21")} 100%)`,
                }}
              />
              {/* North wall — closes the back of the body */}
              <div
                className="absolute top-0 right-0 left-0 h-[30px]"
                style={{
                  transformOrigin: "top",
                  transform: "rotateX(-90deg)",
                  backgroundColor: shade("#16181c"),
                }}
              />
            </div>

            {/* Roof — exact pyramid over the body, eaves overhanging 8px */}
            <div
              className="absolute -inset-2"
              style={{ transformStyle: "preserve-3d", transform: "translateZ(30px)" }}
            >
              {/* Soffit — the underside plane that closes the eave overhang */}
              <div className="absolute inset-0" style={{ backgroundColor: shade("#16181c") }} />

              {/* North slope */}
              <div
                className="absolute top-0 right-0 left-0 h-[65px]"
                style={{
                  transformOrigin: "top",
                  transform: `rotateX(${ROOF_PITCH}deg)`,
                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                  background: `linear-gradient(90deg, ${shade("#1e2125")} 0%, ${shade("#121417")} 100%)`,
                }}
              />
              {/* South slope */}
              <div
                className="absolute right-0 bottom-0 left-0 h-[65px]"
                style={{
                  transformOrigin: "bottom",
                  transform: `rotateX(-${ROOF_PITCH}deg)`,
                  clipPath: "polygon(0 100%, 100% 100%, 50% 0)",
                  background: `linear-gradient(90deg, ${GREEN}26, transparent 80%), linear-gradient(90deg, ${shade("#272b30")} 0%, ${shade("#1e2125")} 100%)`,
                }}
              />
              {/* West slope — lit side */}
              <div
                className="absolute top-0 bottom-0 left-0 w-[65px]"
                style={{
                  transformOrigin: "left",
                  transform: `rotateY(-${ROOF_PITCH}deg)`,
                  clipPath: "polygon(0 0, 0 100%, 100% 50%)",
                  background: `linear-gradient(180deg, ${GREEN}29, transparent 80%), linear-gradient(180deg, ${shade("#2e3338")} 0%, ${shade("#25292e")} 100%)`,
                }}
              />
              {/* East slope */}
              <div
                className="absolute top-0 right-0 bottom-0 w-[65px]"
                style={{
                  transformOrigin: "right",
                  transform: `rotateY(${ROOF_PITCH}deg)`,
                  clipPath: "polygon(100% 0, 100% 100%, 0 50%)",
                  background: `linear-gradient(180deg, ${shade("#101216")} 0%, ${shade("#1a1d21")} 100%)`,
                }}
              />

              {/* Fascia — roof thickness hanging from each eave */}
              <div
                className="absolute top-full right-0 left-0 h-[5px]"
                style={{
                  transformOrigin: "top",
                  transform: "rotateX(-90deg)",
                  backgroundColor: shade("#1a1d21"),
                }}
              />
              <div
                className="absolute right-0 bottom-full left-0 h-[5px]"
                style={{
                  transformOrigin: "bottom",
                  transform: "rotateX(90deg)",
                  backgroundColor: shade("#16181c"),
                }}
              />
              <div
                className="absolute top-0 bottom-0 left-full w-[5px]"
                style={{
                  transformOrigin: "left",
                  transform: "rotateY(90deg)",
                  backgroundColor: shade("#25292e"),
                }}
              />
              <div
                className="absolute top-0 right-full bottom-0 w-[5px]"
                style={{
                  transformOrigin: "right",
                  transform: "rotateY(-90deg)",
                  backgroundColor: shade("#101216"),
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Location pin — billboard above the roof, bobbing gently */}
      <motion.div
        className="absolute top-1/2 left-1/2 mt-[-88px] ml-[4px]"
        initial={{ y: 0 }}
        animate={active ? { y: [0, -4, 0] } : undefined}
        transition={{ duration: 3.2, delay: 0.55, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* overflow-visible keeps the drop shadow (now on the path, so the mask
            cuts through it too) from being clipped by the SVG viewport */}
        <svg
          viewBox="0 0 24 30"
          className="h-[38px] w-[30px] overflow-visible"
          fill="none"
          aria-hidden="true"
        >
          {/* The centre is a real hole — no paint at all, so the card surface
              shows through and it reads correctly in both themes */}
          <mask id={holeId} maskUnits="userSpaceOnUse" x={-30} y={-30} width={84} height={90}>
            <rect x={-30} y={-30} width={84} height={90} fill="#fff" />
            <circle cx={12} cy={11} r={4} fill="#000" />
          </mask>
          <path
            d="M12 1C6.5 1 2 5.5 2 11c0 7.4 10 17 10 17s10-9.6 10-17C22 5.5 17.5 1 12 1z"
            fill={GREEN}
            mask={`url(#${holeId})`}
            style={{
              filter: `drop-shadow(0 4px 7px rgba(0,0,0,0.16)) drop-shadow(0 0 8px ${GREEN}33)`,
            }}
          />
        </svg>
      </motion.div>
    </div>
  );
}
